import { useEffect, type ReactNode } from "react";
import { axiosInstance } from "@/api/client";
import { tokenStorage } from "@/auth/tokenStorage";
import { useAuthStore, type AuthUser } from "@/auth/authStore";

export function AuthProvider({ children }: { children: ReactNode }) {
  const setAuthenticating = useAuthStore((s) => s.setAuthenticating);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  useEffect(() => {
    function handleForcedLogout() {
      tokenStorage.clear();
      setUnauthenticated();
    }
    window.addEventListener("auth:logout", handleForcedLogout);
    return () => window.removeEventListener("auth:logout", handleForcedLogout);
  }, [setUnauthenticated]);

  // --- Idle Timeout (Proactive Logout after 15 mins of inactivity) ---
  useEffect(() => {
    const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    let idleTimer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // If the user has been inactive for 15 minutes, force logout.
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }, IDLE_TIMEOUT_MS);
    };

    // Attach listeners to detect activity
    const events = ["mousemove", "keydown", "wheel", "click", "touchstart"];
    events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));
    
    // Start timer initially
    resetTimer();

    return () => {
      clearTimeout(idleTimer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        setUnauthenticated();
        return;
      }

      setAuthenticating();
      try {
        const refreshRes = await axiosInstance.post<{ access_token: string; refresh_token?: string }>(
          "/api/v1/identity/refresh/",
          { refresh_token: refreshToken }
        );
        tokenStorage.setAccessToken(refreshRes.data.access_token);
        if (refreshRes.data.refresh_token) {
          tokenStorage.setRefreshToken(refreshRes.data.refresh_token);
        }

        const meRes = await axiosInstance.get<AuthUser>("/api/v1/identity/me/");
        if (!cancelled) setAuthenticated(meRes.data);
      } catch {
        if (!cancelled) {
          tokenStorage.clear();
          setUnauthenticated();
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [setAuthenticating, setAuthenticated, setUnauthenticated]);

  return <>{children}</>;
}
